import { useAppSelector } from 'dux/app/hooks';

import { theme } from '@prose-ui';
import { legacyTheme, styled } from '@prose-ui/legacy';

import { Typography } from 'Components/Typography';

import { productsSlugs } from 'constants/products';

import { moduleFormula } from 'assets/content/landingPages/supplements';

import { formatPriceWithCurrency } from 'utils/currencies';
import useResponsiveVariant from 'utils/useResponsiveVariant';

import { getProductPricesContentFromAPI } from 'dux/products/selectors';

import { CTA } from './CTA';

const Root = styled.section<{ position?: 'fixed' | 'relative' }>`
  position: ${(props) => props.position ?? 'initial'};
  top: 0;
  left: 0;
  z-index: 1;

  width: 100vw;
  height: 100vh;
  min-height: 775px;
  margin-top: ${(props) => (props.position === 'fixed' ? '95px' : 0)};
`;

const Content = styled.div`
  position: absolute;
  top: 0;
  right: 0;

  padding: 40px 24px;

  ${legacyTheme.breakpoints.up('sm')} {
    padding: 104px 72px;
  }

  ${legacyTheme.breakpoints.up('md')} {
    top: 10%;

    display: flex;
    flex-direction: column;
    align-items: center;

    width: 50%;
    margin-left: auto;
    padding: 170px 5px;
  }
`;

const Title = styled(Typography)`
  font-family: ${legacyTheme.typography.simplonNorm};
  font-size: 24px;

  & sup {
    top: -0.7em;
    font-size: 50%;
    font-weight: 500;
  }
`;

const Paragraph = styled(Typography)`
  letter-spacing: normal;
  line-height: 1.4;
  margin-bottom: 32px;

  ${legacyTheme.breakpoints.up('md')} {
    width: 500px;
    margin-right: auto;
    margin-left: auto;
  }
`;

const Prices = styled.div`
  display: flex;
  max-width: 430px;
  margin-bottom: 24px;

  border-top: 1px solid ${theme.colors.primary[400]};
  border-bottom: 1px solid ${theme.colors.primary[400]};
`;

const Formula = styled.div`
  width: 50%;
  padding: 15px 0 10px;

  &:first-of-type {
    border-right: 1px solid ${theme.colors.primary[400]};
  }

  &:last-of-type {
    margin-left: 24px;
  }
`;

const Image = styled.img`
  display: block;
  width: 100%;
  height: 100%;

  ${legacyTheme.breakpoints.up('md')} {
    object-fit: cover;
  }
`;

const Label = styled(Typography)`
  margin-bottom: 1px;

  font-weight: 600;
  line-height: 1.38;
  letter-spacing: 0.57px;
`;

const Price = styled(Typography)`
  letter-spacing: 0.5px;
`;

const Info = styled(Typography)`
  letter-spacing: 0.5px;

  &:last-of-type {
    margin-bottom: 32px;
  }
`;

type ModuleFormulaProps = {
  position: 'fixed' | 'relative';
};

export const ModuleFormula = ({ position = 'fixed' }: ModuleFormulaProps) => {
  const { isMobile, isTablet } = useResponsiveVariant();

  const supplementsPricesContentFromAPI: {
    currency: string;
    price: number;
    subscribed_price: number;
  } | null = useAppSelector((state) =>
    getProductPricesContentFromAPI(state, {
      productSlug: productsSlugs.SUPPLEMENT_CORE,
    }),
  );

  const imgSrc = isMobile
    ? moduleFormula.image.mobile
    : isTablet
    ? moduleFormula.image.tablet
    : moduleFormula.image.desktop;

  return (
    <Root data-testid="supplements-landing-module" position={position}>
      <Image
        alt="2 supplements. Each jar contains a specific capsule. Capsule 1 for scalp balance. Capsule 2 for hair growth."
        src={imgSrc.src}
      />

      <Content>
        <div>
          <Title align="left" color="noir" paragraph variant="h2">
            {moduleFormula.title}
          </Title>

          <Paragraph markupName="p" variant="p1">
            {moduleFormula.paragraph}
          </Paragraph>

          <Prices>
            {moduleFormula.prices.map((el, index) => (
              <Formula key={el.formula}>
                <Label variant="p1">{el.formula}</Label>

                <Price variant="p1">
                  {supplementsPricesContentFromAPI &&
                    formatPriceWithCurrency({
                      price:
                        index === 0
                          ? supplementsPricesContentFromAPI.subscribed_price
                          : supplementsPricesContentFromAPI.price,
                      currency: supplementsPricesContentFromAPI.currency,
                    })}
                </Price>
              </Formula>
            ))}
          </Prices>

          <Paragraph markupName="p" variant="p1">
            {moduleFormula.info.map((el, key) => (
              <Info key={String(key)}>{el}</Info>
            ))}
          </Paragraph>

          {!isMobile && <CTA large />}
        </div>
      </Content>
    </Root>
  );
};
